/**
 * `OrderStatus` — ciclo de **fulfillment** do pedido (CONTRACTS §2.1, DOMAIN §4.2).
 *
 * **12 estados de fulfillment, 8 no MVP.** Devolucao vive em `ReturnRequest.status`;
 * reembolso vive em `Refund.status`; estado financeiro em `PaymentStatus`. O ciclo
 * financeiro e **separado** do de fulfillment e nao mora neste modulo (X-01).
 */

export const ORDER_STATUSES = [
  'AGUARDANDO_PAGAMENTO',
  'AGUARDANDO_RECEITA',
  'AGUARDANDO_ACEITE',
  'EM_SEPARACAO',
  'AJUSTE_PENDENTE',
  'PRONTO',
  'EM_ROTA',
  'ENTREGUE',
  'RETIRADO',
  'NAO_ENTREGUE',
  'DEVOLVIDO_A_LOJA',
  'CANCELADO',
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

/**
 * Recorte do MVP (D8, CONTRACTS §2.1): **8 estados**.
 * Retirada concluida no MVP = `ENTREGUE` com `fulfillmentMode = PICKUP`.
 */
export const MVP_ORDER_STATUSES = [
  'AGUARDANDO_PAGAMENTO',
  'AGUARDANDO_ACEITE',
  'EM_SEPARACAO',
  'AJUSTE_PENDENTE',
  'PRONTO',
  'EM_ROTA',
  'ENTREGUE',
  'CANCELADO',
] as const satisfies readonly OrderStatus[];

/** Existem no enum do banco desde a primeira migracao; a aplicacao nao os produz no MVP. */
export const PLANNED_ORDER_STATUSES = [
  'AGUARDANDO_RECEITA',
  'RETIRADO',
  'NAO_ENTREGUE',
  'DEVOLVIDO_A_LOJA',
] as const satisfies readonly OrderStatus[];

export const TERMINAL_ORDER_STATUSES = [
  'ENTREGUE',
  'RETIRADO',
  'CANCELADO',
] as const satisfies readonly OrderStatus[];

/** X-42 — o estado inicial e sempre explicito na criacao; sem `@default` na coluna. */
export const INITIAL_ORDER_STATUSES = [
  'AGUARDANDO_PAGAMENTO',
  'AGUARDANDO_RECEITA',
  'AGUARDANDO_ACEITE',
] as const satisfies readonly OrderStatus[];

/**
 * X-01 — valores que **nunca** existirao em `OrderStatus`. Estao aqui, e testados, porque
 * cada um deles ja apareceu em algum documento e a regressao e barata de cometer.
 */
export const FORBIDDEN_IN_ORDER_STATUS = [
  'REEMBOLSADO',
  'EM_DEVOLUCAO',
  'DEVOLVIDO',
  'PAGAMENTO_EXPIRADO',
  'EXPIRADO',
  'EM_CONFERENCIA',
  'CONFERINDO',
] as const;

/** Lista fechada, obrigatoria em `CANCELADO` (CONTRACTS §2.1, DOMAIN §4.2). */
export const CANCELLATION_REASONS = [
  'PIX_EXPIRADO',
  'CLIENTE_DESISTIU',
  'LOJA_RECUSOU',
  'SLA_ACEITE_ESTOURADO',
  'RUPTURA_TOTAL',
  'AJUSTE_RECUSADO',
  'RECEITA_REPROVADA',
  'RECEITA_NAO_ENVIADA',
  'FORA_DE_AREA',
  'NAO_RETIRADO_NO_PRAZO',
  'ENDERECO_NAO_LOCALIZADO',
  'SUSPEITA_DE_FRAUDE',
  'ERRO_OPERACIONAL',
] as const;

export type CancellationReason = (typeof CANCELLATION_REASONS)[number];

/** CONTRACTS §7.2 (X-17) — enum canonico de `OrderAdjustment.defaultOnTimeout`. */
export const ADJUSTMENT_TIMEOUT_DEFAULTS = ['ENVIAR_PARCIAL', 'CANCELAR'] as const;

export type AdjustmentTimeoutDefault = (typeof ADJUSTMENT_TIMEOUT_DEFAULTS)[number];

export const FULFILLMENT_MODES = ['DELIVERY', 'PICKUP'] as const;

export type FulfillmentMode = (typeof FULFILLMENT_MODES)[number];

/** CONTRACTS §2.5 (D9) — pedido assistido e canal de primeira classe do MVP. */
export const ORDER_CHANNELS = ['PWA', 'WHATSAPP_ASSISTIDO', 'BALCAO'] as const;

export type OrderChannel = (typeof ORDER_CHANNELS)[number];

const CONJUNTO_ESTADOS: ReadonlySet<string> = new Set<string>(ORDER_STATUSES);
const CONJUNTO_MVP: ReadonlySet<string> = new Set<string>(MVP_ORDER_STATUSES);
const CONJUNTO_TERMINAIS: ReadonlySet<string> = new Set<string>(TERMINAL_ORDER_STATUSES);
const CONJUNTO_MOTIVOS: ReadonlySet<string> = new Set<string>(CANCELLATION_REASONS);

export function isOrderStatus(value: unknown): value is OrderStatus {
  return typeof value === 'string' && CONJUNTO_ESTADOS.has(value);
}

export function isMvpOrderStatus(value: string): boolean {
  return CONJUNTO_MVP.has(value);
}

export function isTerminalOrderStatus(value: string): boolean {
  return CONJUNTO_TERMINAIS.has(value);
}

export function isCancellationReason(value: unknown): value is CancellationReason {
  return typeof value === 'string' && CONJUNTO_MOTIVOS.has(value);
}
